import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";

interface MobileStoryCompactPairProps {
  stories: ExploreStoryCard[];
  exploreBase: string;
}

/**
 * Two compact cards side by side — Quartz/Product Hunt style.
 * Square image + short headline + category + time.
 * Creates a visual rhythm break in the feed.
 */
export function MobileStoryCompactPair({
  stories,
  exploreBase,
}: MobileStoryCompactPairProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {stories.map((story) => (
        <CompactMiniCard
          exploreBase={exploreBase}
          key={story.storyId}
          story={story}
        />
      ))}
    </div>
  );
}

function CompactMiniCard({
  story,
  exploreBase,
}: {
  story: ExploreStoryCard;
  exploreBase: string;
}) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <Link
      className="group flex flex-col overflow-hidden rounded-xl bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow)] active:scale-[0.98]"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Square image */}
      <div className="relative mx-1.5 mt-1.5 aspect-[4/3] overflow-hidden rounded-t-lg bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        <Image
          alt=""
          className="h-full w-full object-cover"
          fill
          sizes="(max-width: 640px) 50vw, 25vw"
          src={story.coverImageUrl}
          unoptimized={!shouldOptimizeImage}
        />
        {story.isNew && (
          <div className="absolute top-1.5 left-1.5 h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_2px_var(--explore-new-indicator-ring)]" />
        )}
      </div>

      {/* Compact text */}
      <div className="flex flex-1 flex-col px-2.5 pt-2 pb-2.5">
        <h3
          className="mb-1.5 line-clamp-3 text-pretty font-semibold text-[0.88rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        <div className="mt-auto flex items-center gap-1 text-[0.65rem] text-[var(--explore-text-muted)]">
          {primaryTag && (
            <span className="truncate font-medium">
              {primaryTag.displayName}
            </span>
          )}
          {primaryTag && <span>·</span>}
          <span className="shrink-0">{story.timeAgo}</span>
        </div>
      </div>
    </Link>
  );
}
